// Country names, flags and detection, derived from the browser's built-in Intl
// region data so no country list is maintained by hand.

// ISO 3166-1 codes that are not countries: the user-assigned ranges (AA,
// QM-QZ, XA-XZ, ZZ) and the exceptionally reserved codes ICU also names.
// XK (Kosovo) is user-assigned but in real use, so it is kept.
const NON_COUNTRY_CODE = /^(AA|Q[M-Z]|X[A-JL-Z]|ZZ|EU|EZ|UN|AC|CP|CQ|DG|EA|IC|TA)$/;

const regionNames = (() => {
  try {
    return new Intl.DisplayNames(['en'], { type: 'region', fallback: 'none' });
  } catch {
    return null;
  }
})();

/** Canonical ISO alpha-2 for a code, e.g. the legacy "UK" becomes "GB". */
export function canonicalCountryCode(code: string): string {
  const upper = code.trim().toUpperCase();
  try {
    return new Intl.Locale(`und-${upper}`).region ?? upper;
  } catch {
    return upper;
  }
}

export function countryName(code: string): string {
  const canonical = canonicalCountryCode(code);
  return regionNames?.of(canonical) ?? canonical;
}

export function countryFlag(code: string): string {
  const canonical = canonicalCountryCode(code);
  if (!/^[A-Z]{2}$/.test(canonical)) return '';
  return String.fromCodePoint(...[...canonical].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

export interface CountryOption { code: string; name: string; flag: string }

export function toCountryOptions(codes: string[]): CountryOption[] {
  return codes
    .map((code) => canonicalCountryCode(code))
    .filter((code, i, all) => all.indexOf(code) === i)
    .map((code) => ({ code, name: countryName(code), flag: countryFlag(code) }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

let allCountriesCache: CountryOption[] | null = null;

/** Every country the browser knows a name for. */
export function allCountries(): CountryOption[] {
  if (allCountriesCache) return allCountriesCache;
  const codes: string[] = [];
  for (let a = 65; a <= 90; a++) {
    for (let b = 65; b <= 90; b++) {
      const code = String.fromCharCode(a, b);
      if (NON_COUNTRY_CODE.test(code) || canonicalCountryCode(code) !== code) continue;
      if (regionNames?.of(code)) codes.push(code);
    }
  }
  allCountriesCache = toCountryOptions(codes);
  return allCountriesCache;
}

/** The visitor's country from their browser locale (e.g. "fr-FR" -> "FR"), if it states one. */
export function browserCountry(): string | null {
  const locales = typeof navigator !== 'undefined' ? navigator.languages ?? [navigator.language] : [];
  for (const locale of locales) {
    try {
      const region = new Intl.Locale(locale).region;
      if (region && /^[A-Z]{2}$/.test(region)) return region;
    } catch {
      // ignore malformed locale tags
    }
  }
  return null;
}
